/**
 * POTray — collapsed/expanded PO Builder staging widget rendered at the
 * bottom of BuyingSession.tsx. Toggles between a 1-row collapsed summary
 * and a full table view of staged items.
 *
 * Tray contents are user-scoped, shared across all of a user's open buying
 * sessions. See usePOStaging.ts for full rationale and
 * ARCHITECTURE_DECISIONS.md Decision 30.
 */
import { useState, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronUp, ChevronDown, ShoppingCart, X, Minus, Plus } from 'lucide-react';
import clsx from 'clsx';
import type { POStagingItem } from '../../hooks/usePOStaging';

function fmt$(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

interface POTrayProps {
  open: boolean;
  onToggle: () => void;
  items: POStagingItem[];
  onUpdateQty: (stagingId: string, qty: number) => void;
  onRemoveItem: (stagingId: string) => void;
  onClearAll: () => void;
  loading: boolean;
  /**
   * Day 81 P5.6 (F51) — distributor active in the current buying session.
   * Drives the per-row "Other dist." badge: fires when an item's
   * distributor_id is non-NULL AND differs from this. NULL distributor_id
   * on the item never fires the badge (charity-match — agrees with F52
   * RPC filter semantic).
   */
  currentSessionDistributorId: string | null;
  /**
   * Day 98 (P8.5) Checkpoint A — the live in-session constraint values
   * (max_spend / max_units_per_sku / min_order_qty / my_products_only). Passed
   * to PO Builder via navigation state on "Build PO" so the caps the partner
   * set this session are authoritative at submit; PO Builder falls back to the
   * user's saved defaults when this is absent.
   */
  constraints?: Record<string, string | undefined>;
  /** Day 98.1b §10 — live in-session guardrail caps, forwarded to PO Builder
   *  via nav-state alongside constraints (advisory; never blocks). */
  guardrails?: Record<string, string | undefined>;
}

// ── POTray ───────────────────────────────────────────────────────────────────

export default function POTray({
  open,
  onToggle,
  items,
  onUpdateQty,
  onRemoveItem,
  onClearAll,
  loading,
  currentSessionDistributorId,
  constraints,
  guardrails,
}: POTrayProps): JSX.Element {
  const navigate = useNavigate();
  const [confirmClear, setConfirmClear] = useState<boolean>(false);

  // Compute summary
  const totalSkus = items.length;
  const totalUnits = items.reduce((s, item) => s + (item.quantity || 0), 0);
  const totalCost = items.reduce((s, item) => {
    const cost = Number(item.cost_per_unit) || Number(item.products?.cost_usd) || 0;
    return s + (item.quantity || 0) * cost;
  }, 0);

  // Collapsed state
  if (!open) {
    return (
      <button
        onClick={onToggle}
        className="h-8 flex-shrink-0 bg-surface-1 border-t border-border flex items-center
                   px-4 gap-4 hover:bg-surface-3 transition-colors cursor-pointer w-full"
      >
        <ShoppingCart size={12} className="text-ink-muted" />
        <span className="text-xs text-ink-secondary">
          PO Builder
          <span
            data-testid="po-tray-summary"
            className={clsx('ml-2 font-medium', totalSkus > 0 ? 'text-accent' : 'text-ink-muted')}
          >
            {totalSkus} item{totalSkus !== 1 ? 's' : ''} · {fmt$(totalCost)}
          </span>
        </span>
        <ChevronUp size={12} className="text-ink-muted ml-auto" />
      </button>
    );
  }

  return (
    <div className="h-52 flex-shrink-0 bg-surface-1 border-t border-border flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-1.5 border-b border-border-subtle flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={onToggle}
            className="p-1 text-ink-muted hover:text-ink-primary"
            aria-label="Collapse PO tray"
          >
            <ChevronDown size={12} />
          </button>
          <span className="text-xs font-semibold text-ink-bright">PO Builder Staging</span>
          {totalSkus > 0 && (
            <span className="text-2xs text-ink-muted">
              {totalSkus} SKU{totalSkus !== 1 ? 's' : ''} · {totalUnits} units · {fmt$(totalCost)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {confirmClear ? (
            <div className="flex items-center gap-1.5">
              <span className="text-2xs text-danger">Clear all?</span>
              <button
                onClick={() => {
                  onClearAll();
                  setConfirmClear(false);
                }}
                className="text-2xs text-danger hover:text-danger/80 font-medium"
              >
                Yes
              </button>
              <button
                onClick={() => setConfirmClear(false)}
                className="text-2xs text-ink-muted hover:text-ink-primary"
              >
                No
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmClear(true)}
              disabled={items.length === 0}
              className="text-2xs text-ink-muted hover:text-danger disabled:opacity-30 transition-colors"
            >
              Clear
            </button>
          )}
          <button
            onClick={() =>
              navigate('/purchasing/po-builder', { state: { constraints, guardrails } })
            }
            disabled={totalUnits === 0}
            className="arbi-btn-primary text-2xs px-3 py-1 disabled:opacity-40"
          >
            Build PO
          </button>
        </div>
      </div>

      {/* Items list */}
      <div className="flex-1 overflow-y-auto pr-2.5">
        {loading ? (
          <div className="flex items-center justify-center h-full text-ink-muted text-xs">
            Loading staged items…
          </div>
        ) : items.length === 0 ? (
          <div className="flex items-center justify-center h-full text-ink-muted text-xs">
            Add products from the table to start building a PO
          </div>
        ) : (
          <table className="w-full text-2xs">
            <thead>
              <tr className="text-ink-muted border-b border-border-subtle bg-surface-2">
                <th className="px-3 py-1.5 text-left font-medium w-24">ISG SKU</th>
                <th className="px-3 py-1.5 text-left font-medium">Title</th>
                <th className="px-3 py-1.5 text-left font-medium w-16">Unit Cost</th>
                <th className="px-3 py-1.5 text-left font-medium w-24">Qty</th>
                <th className="px-3 py-1.5 text-left font-medium w-20">Total</th>
                <th className="px-3 py-1.5 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const cost = Number(item.cost_per_unit) || Number(item.products?.cost_usd) || 0;
                const qty = item.quantity || 0;
                const rowTotal = qty * cost;
                // Day 81 P5.6 (F51) — cross-distributor badge. Fires when
                // the row's distributor_id is non-NULL AND differs from
                // the current session's distributor_id. NULL on either
                // side → no badge (charity-match — agrees with F52 RPC
                // filter semantic so the tray and PO Impact strip don't
                // contradict each other).
                const isDifferentDistributor =
                  item.distributor_id !== null &&
                  currentSessionDistributorId !== null &&
                  item.distributor_id !== currentSessionDistributorId;
                return (
                  <tr key={item.id} className="border-b border-border-subtle hover:bg-surface-3">
                    <td className="px-3 py-1.5 font-mono text-accent">
                      {item.isg_sku}
                      {isDifferentDistributor && (
                        <span
                          className="ml-2 text-2xs px-1.5 py-0.5 rounded bg-warning/15 text-warning border border-warning/30"
                          title="Staged from a different distributor — not included in this session's PO Impact"
                          aria-label="Item from different distributor"
                          data-testid="po-tray-different-distributor-badge"
                        >
                          Other dist.
                        </span>
                      )}
                    </td>
                    <td
                      className="px-3 py-1.5 text-ink-bright truncate max-w-[200px]"
                      title={item.products?.title ?? undefined}
                    >
                      {item.products?.title || '—'}
                    </td>
                    <td className="px-3 py-1.5 text-left font-mono text-ink-secondary">
                      {fmt$(cost)}
                    </td>
                    <td className="px-3 py-1.5 text-left">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => onUpdateQty(item.id, Math.max(1, qty - 1))}
                          className="w-5 h-5 flex items-center justify-center rounded bg-surface-4 border border-border text-ink-secondary hover:text-ink-primary"
                          aria-label="Decrease quantity"
                        >
                          <Minus size={9} />
                        </button>
                        <span className="w-8 text-center text-xs font-mono text-ink-bright">
                          {qty}
                        </span>
                        <button
                          onClick={() => onUpdateQty(item.id, qty + 1)}
                          className="w-5 h-5 flex items-center justify-center rounded bg-surface-4 border border-border text-ink-secondary hover:text-ink-primary"
                          aria-label="Increase quantity"
                        >
                          <Plus size={9} />
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-1.5 text-left font-mono text-ink-bright">
                      {fmt$(rowTotal)}
                    </td>
                    <td className="px-3 py-1.5">
                      <button
                        onClick={() => onRemoveItem(item.id)}
                        className="p-0.5 text-ink-muted hover:text-danger transition-colors"
                        aria-label="Remove item"
                      >
                        <X size={10} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
